// The signed-in person's data in Firestore, as the kit's FirestoreBackend.swift keeps it in step; the kit is the
// reference, and this follows it closely.
//
// Only changes travel. The browser keeps the whole account (cache.ts), with the moment it was last in step; on every
// start it asks Firestore only for what changed since then (`modified > syncedUntil − 60 s`). Every document carries
// `modified`, set by the server; deleted records stay behind as tombstones (`deleted: true`), so other devices learn
// of a deletion through the same question.
//
// - The person's own collections, their document, the shares they are in (each share's four collections with their
//   own `syncedUntil`), invitations waiting for them, and their placements in shares of others.
// - Nothing goes on to the store until every collection has answered from the server once and no write of this
//   browser is still waiting for it; then changes go on gathered for a moment (Firestore reports every write of this
//   browser again, and once more when the server confirms it).
// - Writes: only the fields that changed (changes.ts), in batches of at most 450; a record that moves into or out of
//   a share is written whole at its new place, with a tombstone at the old; a person's own values in a share of
//   someone else go to their placement. A batch the server refuses is read back from the server, so the browser
//   doesn't keep what was refused.
// - Below the account's minimum feature level, nothing is written.

import {
  codecs, decodePlacement, decodeShare, decodeUserDocument, encodeFavorites, isTombstone, momentOf, type Placement,
  type Fields, type Share, type UserDocument,
} from "../core/records";
import { normalizedUUID } from "../core/ids";
import { missingFieldsWrite, placementWrite, recordWrite, SERVER_TIMESTAMP, tombstoneWrite, type Write } from "../core/changes";
import { accountMarks, isBlocked, markFields, marked, WEB_FEATURE_LEVEL, type FeatureMark } from "../core/featureLevel";
import {
  documentPath, isShareable, placedRecord, placementPath, planSaves, rehomed, ShareRouting, type PlacedSave,
  type ShareableCollection,
} from "../core/sharing";
import type { RecordEdits } from "../core/edits";
import { currentZone } from "../core/days";
import { isInStep, type SyncObservation } from "../core/syncStatus";
import {
  applyChanges, byID, emptyAccount, LISTENED_COLLECTIONS, type Account, type AccountCollection, type Changes,
} from "./account";
import { CACHE_FORMAT, type AccountCache, type CachedAccount } from "./cache";
import { decodeInvitation } from "./invitations";
import { remoteError, type Remote, type RemoteError, type RemoteWrite } from "./remote";

/** A write stamped just before `syncedUntil` can become visible just after it: asking a little further back catches it. */
export const OVERLAP_MS = 60_000;
/** A batch holds at most 500 writes; a first upload can hold more. */
export const BATCH_LIMIT = 450;
/** How long changes are gathered before they go on. */
export const FORWARD_DELAY_MS = 250;

/** Listeners whose failure doesn't hold the first whole picture back (ListenerRecovery.nonBlocking). */
const NON_BLOCKING = new Set(["shares", "invitations", "placements"]);
/** Every listener that must answer before the first picture goes on. */
const ALL_LISTENERS: readonly string[] = [...LISTENED_COLLECTIONS, "user", "shares", "invitations", "placements"];

/** Seconds before a failed listener is set up again: 2, 4, 8, … up to five minutes; never for refused permission. */
export function retryDelay(attempt: number, code: string): number | null {
  if (code === "permission-denied") return null;
  return Math.min(2 * 2 ** (Math.max(attempt, 1) - 1), 300);
}

/** Something worth knowing that went wrong; the sync carries on. */
export type SyncProblem =
  | { kind: "unreadable"; collection: string; id: string; error: string }
  | { kind: "listener"; key: string; error: RemoteError }
  | { kind: "refused"; error: RemoteError }
  | { kind: "blocked" }
  | { kind: "cache"; error: unknown };

export interface SyncOptions {
  uid: string;
  remote: Remote;
  cache: AccountCache;
  /** The web's version, kept with the cache. */
  appVersion?: string;
  /** The browser's zone, for old days (default: the browser's own). */
  zone?: () => string;
  /** The level this build shows (default `WEB_FEATURE_LEVEL`). */
  level?: number;
  /** A minimum version for everyone from Remote Config, and the web's own version. */
  globalMinimum?: () => { minimum?: string; current: string } | undefined;
  onProblem?: (problem: SyncProblem) => void;
  /**
   * Called after this browser wrote something, with the account's document as it knows it: the other devices are
   * woken (devicePush.ts).
   */
  onWrite?: (user: UserDocument) => void;
  /** The language the interface shows ("de", "en"), written to the account when it differs (`interfaceLanguage`). */
  interfaceLanguage?: () => string;
  /** Whether there is a network, and how to hear it change (default: the browser's `navigator.onLine`). */
  network?: Network;
  /** The clock (default: `Date.now`). */
  now?: () => number;
}

/** Whether the browser says there is a network. */
export interface Network {
  online(): boolean;
  subscribe(listener: () => void): () => void;
}

/** The browser's own word on the network: `navigator.onLine` and its `online` and `offline` events. */
export const browserNetwork: Network = {
  online: () => typeof navigator === "undefined" || navigator.onLine !== false,
  subscribe(listener) {
    if (typeof window === "undefined") return () => undefined;
    addEventListener("online", listener);
    addEventListener("offline", listener);
    return () => {
      removeEventListener("online", listener);
      removeEventListener("offline", listener);
    };
  },
};

/** Where a written document is, so a refused batch can be read back. `user` is the person's own document. */
interface Written {
  collection: AccountCollection | "user";
  container: string | null;
  id: string;
}

interface PendingWrite {
  path: string;
  write: Write;
  written: Written;
}

export class Sync {
  private readonly uid: string;
  private readonly remote: Remote;
  private readonly cache: AccountCache;
  private readonly options: SyncOptions;
  private onChange: ((account: Account) => void) | null = null;

  /** The whole account as far as this browser knows it. */
  private known: Account = emptyAccount();
  /** What the store was last given, so an unchanged state is not handed on again. */
  private forwarded: Account | null = null;
  private syncedUntil: number | undefined;
  private shareSyncedUntil: Record<string, number> = {};
  /** Which share each shared record came from (own records have none). */
  private containers: Record<string, string> = {};

  private listeners = new Map<string, () => void>();
  private shareListeners = new Map<string, Map<ShareableCollection, () => void>>();
  /** Counts up every time listening starts over: a retry from before then is void. */
  private generation = 0;
  private failures = new Map<string, number>();
  private arrived = new Set<string>();
  /** Listeners whose latest snapshot still holds this browser's own unconfirmed writes. */
  private pending = new Set<string>();
  private forwardTimer: ReturnType<typeof setTimeout> | undefined;
  private retryTimers = new Set<ReturnType<typeof setTimeout>>();
  private stopped = false;
  private hadCache = false;

  private cacheWriting: Promise<void> | null = null;
  private cacheDirty = false;
  /** Writes sent and not yet taken or refused by the server. */
  private inFlight = new Set<Promise<void>>();
  /** Records whose missing fields are being written (`fillMissingFields`). */
  private filling = new Set<string>();

  // Whether this browser is in step (docs/64, `SyncObservation`): listeners whose latest snapshot came from the cache,
  // the batches sent and not yet answered by the server with when each went, since when `pending` holds anything,
  // since when reading has been behind (undefined while in step), and the last moment in step (kept in the cache).
  private fromCache = new Set<string>();
  private waitingBatches = new Map<Promise<void>, number>();
  private pendingSince: number | undefined;
  private readingBehindSince: number | undefined;
  private inStepAt: number | undefined;
  private wasInStep = false;
  private readonly syncListeners = new Set<() => void>();
  private stopNetwork: (() => void) | null = null;

  constructor(options: SyncOptions) {
    this.options = options;
    this.uid = options.uid;
    this.remote = options.remote;
    this.cache = options.cache;
    this.readingBehindSince = this.now();
  }

  private now(): number {
    return (this.options.now ?? Date.now)();
  }

  private get network(): Network {
    return this.options.network ?? browserNetwork;
  }

  private get zone(): string {
    return (this.options.zone ?? currentZone)();
  }

  private get appVersion(): string {
    return this.options.appVersion ?? "dev";
  }

  // MARK: - Starting

  /** What this browser kept of the account last time; null when it never synced it. Call before `start`. */
  async load(): Promise<Account | null> {
    let cached: CachedAccount | null = null;
    try {
      cached = await this.cache.read(this.uid);
    } catch (error) {
      this.report({ kind: "cache", error });
    }
    if (!cached || cached.format !== CACHE_FORMAT) return null;
    this.hadCache = true;
    this.known = { ...emptyAccount(), ...cached.account };
    this.containers = { ...cached.containers };
    this.shareSyncedUntil = { ...cached.shareSyncedUntil };
    this.inStepAt = cached.inStepAt;
    // After an update of the web, everything once more: records the older version couldn't read come in now.
    this.syncedUntil = cached.appVersion === this.appVersion ? cached.syncedUntil : undefined;
    if (cached.appVersion !== this.appVersion) this.shareSyncedUntil = {};
    return this.known;
  }

  /** Starts listening. `onChange` gets the whole account whenever it changed elsewhere. */
  start(onChange: (account: Account) => void): void {
    this.onChange = onChange;
    this.stopped = false;
    this.stopNetwork ??= this.network.subscribe(() => this.noteSync());
    this.listen();
  }

  /** Back after a while away (the tab came to the front): asks again from the newest point. */
  resume(): void {
    if (!this.stopped && this.onChange) this.listen();
  }

  stop(): void {
    this.stopped = true;
    this.stopNetwork?.();
    this.stopNetwork = null;
    for (const stop of this.listeners.values()) stop();
    this.listeners.clear();
    for (const share of this.shareListeners.values()) for (const stop of share.values()) stop();
    this.shareListeners.clear();
    clearTimeout(this.forwardTimer);
    for (const timer of this.retryTimers) clearTimeout(timer);
    this.retryTimers.clear();
    this.generation += 1;
  }

  /** Whether the browser had the account already when it started. */
  get hadCachedAccount(): boolean {
    return this.hadCache;
  }

  /** The account as the sync holds it now. */
  get account(): Account {
    return this.known;
  }

  /** This version may not take part: it writes nothing (the account's minimum, or Remote Config's). */
  get isBlocked(): boolean {
    return isBlocked(accountMarks(this.known.user), this.options.level ?? WEB_FEATURE_LEVEL, this.options.globalMinimum?.());
  }

  /** Every listener answered from the server and no write of this browser is waiting: the account is in step. */
  get isSettled(): boolean {
    return this.arrived.size === ALL_LISTENERS.length && this.pending.size === 0;
  }

  /**
   * Resolves when every write sent so far was taken or refused, and the cache is written. For tests and sign-out.
   * Offline, writes wait for the connection, and so does this.
   */
  async flush(): Promise<void> {
    while (this.inFlight.size > 0) await Promise.allSettled([...this.inFlight]);
    await this.flushCache();
  }

  /** Resolves when the account as it is now is in the cache. */
  async flushCache(): Promise<void> {
    while (this.cacheWriting) await this.cacheWriting;
  }

  // MARK: - Writing

  /**
   * Writes a change. `snapshot` is everything after it, `previous` everything as the store knew it before it: a changed
   * record writes only the fields that differ from that, never from this browser's own copy, which may hold a newer
   * version from elsewhere that the store has not seen yet (it would otherwise be written back the old way).
   */
  apply(changes: Changes, snapshot: Account, previous: Account): void {
    // What the records were before, as far as this browser knows: whether one is new here, and where it lived.
    const before = this.known;
    // The own copy follows at once, so nothing waits for Firestore's echo.
    this.known = applyChanges(this.known, changes);
    this.saveCache();
    // A version too old for the account writes nothing, so it can't damage what newer versions made.
    if (this.isBlocked) {
      this.report({ kind: "blocked" });
      return;
    }
    const writes: PendingWrite[] = [];
    const options = { toStoredMoment: (date: Date) => this.remote.toStoredMoment(date) };

    // Where each record lives: in the person's own data, or in a share (docs/43). A project moved into or out of a
    // share takes its to-dos and sections along, unchanged as they are.
    const routing = new ShareRouting(snapshot.shares, snapshot.projects);
    const routingBefore = new ShareRouting(snapshot.shares, previous.projects);
    const todos = withRehomed(changes.todos, snapshot.todos, routingBefore, routing, (r, todo) => r.ofTodo(todo));
    const sections = withRehomed(changes.sections, snapshot.sections, routingBefore, routing, (r, section) => r.ofSection(section));
    // A share of someone else: this person's own values never go into the share, but into their placement.
    const plan = planSaves(
      { todos: todos.saved, projects: changes.projects?.saved, sections: sections.saved, areas: changes.areas?.saved },
      routing, snapshot.shares, this.uid, this.known.placements,
    );

    const write = <Name extends AccountCollection>(
      name: Name, saved: readonly PlacedSave<Account[Name][number]>[], deleted: readonly string[],
    ) => {
      const shareable = isShareable(name);
      const was = byID<{ id: string }>(before[name]);
      // What the store changed from: its own last version of the record, or this browser's where it has none.
      const base = byID<{ id: string }>(previous[name]);
      for (const { record, shareID, leaveOut } of saved) {
        const id = record.id;
        const container = shareable ? shareID : null;
        const known = was.has(id);
        const oldContainer = shareable && known ? (this.containers[id] ?? null) : container;
        if (shareable) {
          if (container === null) delete this.containers[id];
          else this.containers[id] = container;
        }
        // A record that lived elsewhere moves: whole at its new place, a tombstone where it was.
        if (oldContainer !== container && known) {
          writes.push({ path: documentPath(this.uid, name, id, oldContainer), write: tombstoneWrite(), written: { collection: name, container: oldContainer, id } });
        }
        const old = oldContainer === container ? (base.get(id) ?? was.get(id)) : undefined;
        const change = recordWrite(name, old as never, record as never, { ...options, leaveOut });
        if (change) writes.push({ path: documentPath(this.uid, name, id, container), write: change, written: { collection: name, container, id } });
      }
      for (const id of deleted) {
        const container = shareable ? (this.containers[id] ?? null) : null;
        if (shareable) delete this.containers[id];
        writes.push({ path: documentPath(this.uid, name, id, container), write: tombstoneWrite(), written: { collection: name, container, id } });
      }
    };
    const own = <T>(records: readonly T[] | undefined) => (records ?? []).map((record) => ({ record, shareID: null, leaveOut: [] as readonly string[] }));

    write("todos", plan.todos, todos.deleted);
    write("projects", plan.projects, changes.projects?.deleted ?? []);
    write("areas", plan.areas, changes.areas?.deleted ?? []);
    write("sections", plan.sections, sections.deleted);
    for (const [shareID, placement] of Object.entries(plan.placements)) this.setPlacement(placement, shareID);
    write("templates", own(changes.templates?.saved), changes.templates?.deleted ?? []);
    write("habits", own(changes.habits?.saved), changes.habits?.deleted ?? []);
    write("birthdays", own(changes.birthdays?.saved), changes.birthdays?.deleted ?? []);
    write("situations", own(changes.situations?.saved), changes.situations?.deleted ?? []);
    write("inboxTraces", own(changes.inboxTraces?.saved), changes.inboxTraces?.deleted ?? []);
    if (changes.favorites !== undefined) {
      writes.push({ path: `users/${this.uid}`, write: { kind: "merge", fields: encodeFavorites(changes.favorites) }, written: { collection: "user", container: null, id: "" } });
    }

    for (let start = 0; start < writes.length; start += BATCH_LIMIT) {
      const batch = writes.slice(start, start + BATCH_LIMIT);
      this.send(batch.map(({ path, write: change }) => ({ path, write: change })), (error) => this.refused(batch.map((item) => item.written), error));
    }
    // The iPhone's badge and widgets, and a Watch with Pro, follow within seconds.
    if (writes.length) this.options.onWrite?.(this.known.user);
  }

  /**
   * Writes this person's placement for a share. Only the fields the placement holds are replaced (each whole): what
   * it doesn't know, such as a field a newer version keeps there, stays.
   */
  setPlacement(placement: Placement, shareID: string): void {
    this.known = { ...this.known, placements: { ...this.known.placements, [shareID]: placement } };
    if (this.isBlocked) return;
    const write = placementWrite(placement, { toStoredMoment: (date) => this.remote.toStoredMoment(date) });
    this.send([{ path: placementPath(this.uid, shareID), write }], (error) => this.report({ kind: "refused", error }));
  }

  /**
   * Marks the account as needing at least a feature level (featureLevel.ts): only ever raised, written once. Set by a
   * feature the first time it is used.
   */
  mark(mark: FeatureMark): void {
    const marks = accountMarks(this.known.user);
    const fields = markFields(mark, marks);
    if (!fields) return;
    const next = marked(mark, marks);
    const user = { ...this.known.user };
    if (next.minimum !== undefined) user.minimumFeatureLevel = next.minimum;
    if (next.suggested !== undefined) user.suggestedFeatureLevel = next.suggested;
    this.known = { ...this.known, user };
    this.send([{ path: `users/${this.uid}`, write: { kind: "merge", fields } }], (error) => this.report({ kind: "refused", error }));
  }

  /**
   * Marks the account: its areas got their icons (docs/61, the store's `addAreaIcons`), here or on another device; no
   * device does it again. The server's time, merged into the person's document.
   */
  markAreaIconsAdded(): void {
    if (this.isBlocked) return;
    this.known = { ...this.known, user: { ...this.known.user, areaIconsAdded: true } };
    this.send([{ path: `users/${this.uid}`, write: { kind: "merge", fields: { areaIconsAddedAt: SERVER_TIMESTAMP } } }], (error) => this.report({ kind: "refused", error }));
  }

  /**
   * Writes the interface's language to the account when the server's copy says otherwise, so the server's own texts
   * (mail, invitations) come in it while the account's language is "Automatic". Called when the person's document
   * arrives from the server and when the language changes.
   */
  languageChanged(): void {
    const language = this.options.interfaceLanguage?.();
    if (!language || !this.arrived.has("user") || this.pending.has("user") || this.isBlocked) return;
    if (this.known.user.interfaceLanguage === language) return;
    this.known = { ...this.known, user: { ...this.known.user, interfaceLanguage: language } };
    this.send([{ path: `users/${this.uid}`, write: { kind: "merge", fields: { interfaceLanguage: language } } }], (error) => this.report({ kind: "refused", error }));
  }

  private send(writes: RemoteWrite[], refused: (error: RemoteError) => void): void {
    const sent = this.remote.commit(writes).catch((error: unknown) => refused(remoteError(error)));
    this.inFlight.add(sent);
    // Answered only once the server took or refused it: offline, it waits (docs/64).
    this.waitingBatches.set(sent, this.now());
    this.noteSync();
    void sent.finally(() => {
      this.inFlight.delete(sent);
      this.waitingBatches.delete(sent);
      this.noteSync();
    });
  }

  // MARK: - Refused writes

  /**
   * The server didn't take a batch (the rules refused it: a member changing what only the owner may, say). This
   * browser applied it to its own copy already; the server's version of every document in it is read back and
   * handed on, so the browser doesn't keep the refused state.
   */
  private refused(documents: readonly Written[], error: RemoteError): void {
    this.report({ kind: "refused", error });
    const reading = this.readBack(documents);
    this.inFlight.add(reading);
    void reading.finally(() => this.inFlight.delete(reading));
  }

  private async readBack(documents: readonly Written[]): Promise<void> {
    const changes: Changes = {};
    const live = new Set<string>();
    const gone: { collection: AccountCollection; id: string }[] = [];
    const edits = <Name extends AccountCollection>(name: Name) =>
      (changes[name] ??= { saved: [], deleted: [] } as never) as RecordEdits<Account[Name][number]>;
    for (const document of documents) {
      try {
        if (document.collection === "user") {
          const data = await this.remote.readFromServer(`users/${this.uid}`);
          const user = decodeUserDocument(data ?? undefined, { zone: this.zone });
          if (user.favorites !== undefined) this.known = { ...this.known, favorites: user.favorites };
          continue;
        }
        const name = document.collection;
        const data = await this.remote.readFromServer(documentPath(this.uid, name, document.id, document.container));
        const shareable = isShareable(name);
        if (data !== null && !isTombstone(data)) {
          if (shareable) {
            if (document.container === null) delete this.containers[document.id];
            else this.containers[document.id] = document.container;
          }
          live.add(document.id);
          const decoded = codecs[name].decode(data, { zone: this.zone });
          if (decoded.ok) {
            const record = document.container === null ? decoded.value : this.placed(name, decoded.value, document.container);
            edits(name).saved.push(record as never);
          }
        } else if (!shareable || (this.containers[document.id] ?? null) === document.container) {
          // Not where the browser thinks it lives: gone, unless another place in the batch still has it.
          gone.push({ collection: name, id: document.id });
        }
      } catch {
        // Offline again, or not readable: what the browser has stays until the next change from the server.
      }
    }
    for (const { collection, id } of gone) {
      if (live.has(id)) continue;
      if (isShareable(collection)) delete this.containers[id];
      edits(collection).deleted.push(id);
    }
    this.known = applyChanges(this.known, changes);
    this.forwardSoon();
  }

  // MARK: - Listening

  private listen(): void {
    for (const stop of this.listeners.values()) stop();
    this.listeners.clear();
    this.arrived.clear();
    this.pending.clear();
    this.failures.clear();
    this.fromCache.clear();
    for (const timer of this.retryTimers) clearTimeout(timer);
    this.retryTimers.clear();
    this.generation += 1;
    this.noteSync();
    for (const key of ALL_LISTENERS) this.listeners.set(key, this.listener(key));
    // The shares' listeners start over too: one that failed while away comes back with the rest.
    for (const shareID of [...this.shareListeners.keys()]) {
      this.stopShareListeners(shareID);
      this.startListening(shareID);
    }
  }

  private since(until: number | undefined): Date | undefined {
    return until === undefined ? undefined : new Date(until - OVERLAP_MS);
  }

  /** The listener on one of the person's own collections, their document, or the sharing collections. */
  private listener(key: string): () => void {
    switch (key) {
      case "user": return this.listenToUser();
      case "shares": return this.listenToShares();
      case "invitations": return this.listenToInvitations();
      case "placements": return this.listenToPlacements();
      default: return this.listenToCollection(key as AccountCollection);
    }
  }

  /** What changed in one collection since `syncedUntil`, or all of it in a browser that never synced. */
  private listenToCollection(name: AccountCollection): () => void {
    const shareable = isShareable(name);
    return this.remote.listen({ path: `users/${this.uid}/${name}`, modifiedAfter: this.since(this.syncedUntil) }, (snapshot) => {
      this.failures.delete(name);
      const saved: { id: string }[] = [];
      const deleted: string[] = [];
      // Only additions and changes count. A document also leaves this query while its own `modified` waits for the
      // server; that is no deletion. Deletions arrive as tombstones.
      for (const change of snapshot.changes) {
        if (change.type === "removed") continue;
        const { data } = change.doc;
        const stamp = momentOf(data.modified);
        if (stamp) this.syncedUntil = Math.max(this.syncedUntil ?? 0, stamp.getTime());
        const id = normalizedUUID(change.doc.id);
        if (isTombstone(data)) {
          // A record that moved into a share leaves a tombstone here: the share has it now.
          if (id && this.containers[id] === undefined) deleted.push(id);
          continue;
        }
        const decoded = codecs[name].decode(data, { zone: this.zone });
        if (!decoded.ok) {
          // Skipped and reported. Only fields it can't be read without come back, from this browser's copy.
          this.report({ kind: "unreadable", collection: name, id: change.doc.id, error: decoded.error });
          if (id && this.containers[id] === undefined) void this.fillMissingFields(name, id);
          continue;
        }
        saved.push(decoded.value);
        // Back in the person's own data (a share stopped): it lives here again.
        if (shareable) delete this.containers[decoded.value.id];
      }
      if (saved.length > 0 || deleted.length > 0) this.known = applyChanges(this.known, { [name]: { saved, deleted } });
      this.received(name, !snapshot.fromCache, snapshot.hasPendingWrites);
    }, (error) => this.failed(name, error));
  }

  /** The person's own document: favorites, the account's marks, Pro as the server last found it. Read whole. */
  private listenToUser(): () => void {
    return this.remote.listenToDocument(`users/${this.uid}`, (snapshot) => {
      this.failures.delete("user");
      const user = decodeUserDocument(snapshot.data ?? undefined, { zone: this.zone });
      // A list of favorites the web can't read keeps what it had.
      const favorites = snapshot.data === null ? this.known.favorites : (user.favorites ?? this.known.favorites);
      this.known = { ...this.known, user, favorites };
      this.received("user", !snapshot.fromCache, snapshot.hasPendingWrites);
      if (!snapshot.fromCache && !snapshot.hasPendingWrites) this.languageChanged();
    }, (error) => this.failed("user", error));
  }

  /** The shares the person is in. A new one is read whole; one they left, or that stopped, takes its records along. */
  private listenToShares(): () => void {
    return this.remote.listen({ path: "shares", where: { field: "memberUIDs", op: "array-contains", value: this.uid } }, (snapshot) => {
      this.failures.delete("shares");
      const shares: Share[] = [];
      for (const doc of snapshot.docs) {
        const decoded = decodeShare(doc.id, doc.data, { zone: this.zone });
        if (decoded.ok) shares.push(decoded.value);
      }
      this.known = { ...this.known, shares };
      const ids = new Set(shares.map((share) => share.id));
      for (const gone of [...this.shareListeners.keys()]) if (!ids.has(gone)) this.stopListening(gone);
      for (const share of shares) if (!this.shareListeners.has(share.id)) this.startListening(share.id);
      this.received("shares", !snapshot.fromCache, snapshot.hasPendingWrites);
    }, (error) => this.failed("shares", error));
  }

  private startListening(shareID: string): void {
    const listeners = new Map<ShareableCollection, () => void>();
    for (const name of SHAREABLE) listeners.set(name, this.listenToShare(shareID, name));
    this.shareListeners.set(shareID, listeners);
  }

  private stopShareListeners(shareID: string): void {
    for (const stop of this.shareListeners.get(shareID)?.values() ?? []) stop();
    this.shareListeners.delete(shareID);
  }

  /** Left, removed or stopped: its records go from this browser (an owner's come back into their own data). */
  private stopListening(shareID: string): void {
    this.stopShareListeners(shareID);
    delete this.shareSyncedUntil[shareID];
    const gone = new Set(Object.entries(this.containers).filter(([, share]) => share === shareID).map(([id]) => id));
    for (const id of gone) delete this.containers[id];
    const placements = { ...this.known.placements };
    delete placements[shareID];
    this.known = {
      ...this.known,
      todos: this.known.todos.filter((record) => !gone.has(record.id)),
      projects: this.known.projects.filter((record) => !gone.has(record.id)),
      areas: this.known.areas.filter((record) => !gone.has(record.id)),
      sections: this.known.sections.filter((record) => !gone.has(record.id)),
      placements,
    };
    this.forwardSoon();
  }

  /** One collection of a share, from where this browser last was in it. */
  private listenToShare(shareID: string, name: ShareableCollection): () => void {
    const key = `${shareID}/${name}`;
    return this.remote.listen({ path: `shares/${shareID}/${name}`, modifiedAfter: this.since(this.shareSyncedUntil[shareID]) }, (snapshot) => {
      this.failures.delete(key);
      const saved: { id: string }[] = [];
      const deleted: string[] = [];
      for (const change of snapshot.changes) {
        if (change.type === "removed") continue;
        const id = normalizedUUID(change.doc.id);
        if (!id) continue;
        const { data } = change.doc;
        const stamp = momentOf(data.modified);
        if (stamp) this.shareSyncedUntil[shareID] = Math.max(this.shareSyncedUntil[shareID] ?? 0, stamp.getTime());
        if (isTombstone(data)) {
          // Only if it still lives in this share: one moved out has its new place already.
          if ((this.containers[id] === shareID || this.containers[id] === undefined) && !this.ownHas(id, name)) {
            deleted.push(id);
            delete this.containers[id];
          }
          continue;
        }
        const decoded = codecs[name].decode(data, { zone: this.zone });
        if (!decoded.ok) {
          this.report({ kind: "unreadable", collection: key, id: change.doc.id, error: decoded.error });
          continue;
        }
        this.containers[id] = shareID;
        saved.push(this.placed(name, decoded.value, shareID));
      }
      if (saved.length > 0 || deleted.length > 0) this.known = applyChanges(this.known, { [name]: { saved, deleted } });
      this.forwardSoon();
      // A share's listener that failed and answers again (docs/64).
      this.noteSync();
    }, (error) => this.failed(key, error));
  }

  /** Whether the person's own data holds the record (live): a share's tombstone then is not its end. */
  private ownHas(id: string, name: ShareableCollection): boolean {
    if (this.containers[id] !== undefined) return false;
    return (this.known[name] as readonly { id: string }[]).some((record) => record.id === id);
  }

  /**
   * A record of the person's own that can't be read: when it only misses fields it needs (an edit that landed on a
   * tombstone, 2026-10-27) and this browser has it, those fields are written from its copy (`missingFieldsWrite`).
   * Decided on the server's version, not on what this browser's own waiting write shows. Not in shares: a member's
   * copy holds their own values, which never go into the share.
   */
  private async fillMissingFields(name: AccountCollection, id: string): Promise<void> {
    if (this.isBlocked || this.filling.has(id)) return;
    const own = (this.known[name] as readonly { id: string }[]).find((record) => record.id === id);
    if (!own) return;
    this.filling.add(id);
    try {
      const path = documentPath(this.uid, name, id);
      const stored = await this.remote.readFromServer(path);
      if (stored === null || isTombstone(stored)) return;
      const codec = codecs[name] as { encode(record: unknown): Fields; decode(data: Fields, options: { zone: string }): { ok: boolean } };
      const readable = (fields: Fields) => codec.decode(fields, { zone: this.zone }).ok;
      const write = missingFieldsWrite(stored, codec.encode(own), readable, { toStoredMoment: (date) => this.remote.toStoredMoment(date) });
      if (write) this.send([{ path, write }], (error) => this.report({ kind: "refused", error }));
    } catch {
      // Offline again: the next time it arrives, it is tried once more.
    } finally {
      this.filling.delete(id);
    }
  }

  /** A record of a share of someone else, with what is this person's own in place of the owner's (sharing.ts). */
  private placed<T>(name: AccountCollection, record: T, shareID: string): T {
    const share = this.known.shares.find((candidate) => candidate.id === shareID);
    if (!share || (name !== "todos" && name !== "projects" && name !== "sections")) return record;
    return placedRecord(name, record as never, share, this.uid, this.known.placements[shareID]) as T;
  }

  /** Every record of the shares of others, with this person's own values in place (after their placements changed). */
  private placeAll(): void {
    const place = <T extends { id: string }>(name: "todos" | "projects" | "sections", records: T[]) => records.map((record) => {
      const shareID = this.containers[record.id];
      return shareID === undefined ? record : this.placed(name, record, shareID);
    });
    this.known = {
      ...this.known,
      todos: place("todos", this.known.todos),
      projects: place("projects", this.known.projects),
      sections: place("sections", this.known.sections),
    };
  }

  private listenToInvitations(): () => void {
    return this.remote.listen({ path: "invitations", where: { field: "toUID", op: "==", value: this.uid } }, (snapshot) => {
      this.failures.delete("invitations");
      const invitations = snapshot.docs.flatMap((doc) => decodeInvitation(doc.id, doc.data) ?? []);
      this.known = { ...this.known, invitations };
      this.received("invitations", !snapshot.fromCache, snapshot.hasPendingWrites);
    }, (error) => this.failed("invitations", error));
  }

  private listenToPlacements(): () => void {
    return this.remote.listen({ path: `users/${this.uid}/placements` }, (snapshot) => {
      this.failures.delete("placements");
      const placements: Record<string, Placement> = {};
      for (const doc of snapshot.docs) placements[doc.id] = decodePlacement(doc.data, { zone: this.zone });
      this.known = { ...this.known, placements };
      // Shared records of others follow this person's own values.
      this.placeAll();
      this.received("placements", !snapshot.fromCache, snapshot.hasPendingWrites);
    }, (error) => this.failed("placements", error));
  }

  /**
   * A listener reported an error instead of a snapshot (the rules, the network, a token). It is reported, and set up
   * again after a pause that grows with each failure in a row, unless the rules refused it, which no retry changes
   * (the next `resume` tries once more). The sharing collections never hold the first whole picture back.
   */
  private failed(key: string, error: RemoteError): void {
    this.report({ kind: "listener", key, error });
    if (NON_BLOCKING.has(key)) this.received(key, true, false);
    const attempt = (this.failures.get(key) ?? 0) + 1;
    this.failures.set(key, attempt);
    this.noteSync();
    const delay = retryDelay(attempt, error.code);
    if (delay === null) return;
    const generation = this.generation;
    const timer = setTimeout(() => {
      this.retryTimers.delete(timer);
      if (this.generation === generation && !this.stopped) this.reattach(key);
    }, delay * 1000);
    this.retryTimers.add(timer);
  }

  /** Sets one listener up again: of the person's own data, or `shareID/collection` of a share. */
  private reattach(key: string): void {
    if (ALL_LISTENERS.includes(key)) {
      this.listeners.get(key)?.();
      this.listeners.set(key, this.listener(key));
      return;
    }
    const [shareID, name] = key.split("/") as [string, ShareableCollection];
    const share = this.shareListeners.get(shareID);
    if (!share) return;
    share.get(name)?.();
    share.set(name, this.listenToShare(shareID, name));
  }

  // MARK: - Handing on

  private received(key: string, fromServer: boolean, hasPending: boolean): void {
    if (fromServer) this.arrived.add(key);
    if (fromServer) this.fromCache.delete(key);
    else this.fromCache.add(key);
    if (hasPending) this.pending.add(key);
    else this.pending.delete(key);
    this.noteSync();
    this.forwardSoon();
  }

  // MARK: - In step (docs/64)

  /** What the browser knows about being in step, for the rule (core/syncStatus.ts). */
  get syncObservation(): SyncObservation {
    const observation: SyncObservation = { online: this.network.online() };
    if (this.readingBehindSince !== undefined) observation.readingBehindSince = this.readingBehindSince;
    const waiting = this.writeWaitingSince;
    if (waiting !== undefined) observation.writeWaitingSince = waiting;
    if (this.inStepAt !== undefined) observation.inStepAt = this.inStepAt;
    return observation;
  }

  /** Calls `listener` whenever `syncObservation` changed. */
  subscribeSync(listener: () => void): () => void {
    this.syncListeners.add(listener);
    return () => this.syncListeners.delete(listener);
  }

  /**
   * Try Again: every listener anew. Resolves true once the browser is in step, or false after a few seconds.
   */
  async retrySync(): Promise<boolean> {
    if (!this.stopped && this.onChange) this.listen();
    const deadline = this.now() + 10_000;
    while (!isInStep(this.syncObservation) && this.now() < deadline) await new Promise((done) => setTimeout(done, 200));
    return isInStep(this.syncObservation);
  }

  /**
   * Every listener has answered from the server since it last started, the newest answers are not from the cache,
   * none failed, and there is a network.
   */
  private get readsInStep(): boolean {
    return this.network.online() && this.arrived.size === ALL_LISTENERS.length && this.fromCache.size === 0 && this.failures.size === 0;
  }

  private get writeWaitingSince(): number | undefined {
    const moments = [...this.waitingBatches.values()];
    if (this.pendingSince !== undefined) moments.push(this.pendingSince);
    return moments.length ? Math.min(...moments) : undefined;
  }

  /** Follows what changed about being in step, and notes the moment the browser leaves it. */
  private noteSync(): void {
    const now = this.now();
    if (this.readsInStep) this.readingBehindSince = undefined;
    else this.readingBehindSince ??= now;
    if (this.pending.size === 0) this.pendingSince = undefined;
    else this.pendingSince ??= now;
    const inStep = this.readingBehindSince === undefined && this.writeWaitingSince === undefined;
    // In step, or just leaving it: now is the last moment in step.
    if (inStep || this.wasInStep) this.inStepAt = now;
    this.wasInStep = inStep;
    for (const listener of [...this.syncListeners]) listener();
  }

  /** Hands the account on once every collection has answered from the server, gathered for a moment. */
  private forwardSoon(): void {
    if (!this.isSettled || this.stopped) return;
    clearTimeout(this.forwardTimer);
    this.forwardTimer = setTimeout(() => {
      this.forwardTimer = undefined;
      this.saveCache();
      if (this.known === this.forwarded) return;
      this.forwarded = this.known;
      this.onChange?.(this.known);
    }, FORWARD_DELAY_MS);
  }

  // MARK: - The browser's copy

  /** Writes the account to the cache; writes asked for while one is under way are gathered into the next. */
  private saveCache(): void {
    if (this.cacheWriting) {
      this.cacheDirty = true;
      return;
    }
    const cached: CachedAccount = {
      format: CACHE_FORMAT, appVersion: this.appVersion, account: this.known, shareSyncedUntil: { ...this.shareSyncedUntil },
      containers: { ...this.containers },
    };
    if (this.syncedUntil !== undefined) cached.syncedUntil = this.syncedUntil;
    if (this.inStepAt !== undefined) cached.inStepAt = this.inStepAt;
    this.cacheWriting = this.cache.write(this.uid, cached)
      .catch((error: unknown) => this.report({ kind: "cache", error }))
      .finally(() => {
        this.cacheWriting = null;
        if (this.cacheDirty) {
          this.cacheDirty = false;
          this.saveCache();
        }
      });
  }

  private report(problem: SyncProblem): void {
    this.options.onProblem?.(problem);
  }
}

const SHAREABLE: readonly ShareableCollection[] = ["todos", "projects", "areas", "sections"];

/** The change with the records added that move into or out of a share because their project did. */
function withRehomed<T extends { id: string }>(
  edits: RecordEdits<T> | undefined, all: readonly T[], before: ShareRouting, after: ShareRouting,
  shareOf: (routing: ShareRouting, record: T) => string | null,
): RecordEdits<T> {
  const saved = edits?.saved ?? [];
  const deleted = edits?.deleted ?? [];
  const touched = new Set([...saved.map((record) => record.id), ...deleted]);
  return { saved: [...saved, ...rehomed(all, touched, before, after, shareOf)], deleted };
}
